import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  addCategoryToFirebase,
  renameCategoryInFirebase,
  removeCategoryFromFirebase,
} from "../firebase";
import {
  collection, getDocs,
  query, where, writeBatch,
} from "firebase/firestore";

function Interviewform({
  categories,          // initial list from parent
  editItem,
  onSubmit,
  onUpdate,
  onCancelEdit,
  onCategoryAdded,
  onSearchResult,
  onCategoriesChanged, // optional — parent can wire this to stay in sync
}) {
  const [form, setForm]               = useState({ category: "" });
  const [blocks, setBlocks]           = useState([{ type: "text", value: "" }]);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading]         = useState(false);
  const [searchText, setSearchText]   = useState("");
  const [searchDropdown, setSearchDropdown] = useState(false);
  const [allData, setAllData]         = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  // ✅ Local copy — updates instantly so UI reflects rename/delete without a refresh
  const [localCategories, setLocalCategories] = useState(categories || []);

  // Keep in sync if parent re-fetches and passes a new list (e.g. on mount)
  useEffect(() => {
    setLocalCategories(categories || []);
  }, [categories]);

  // Category manager
  const [showCatManager, setShowCatManager]     = useState(false);
  const [renamingCat, setRenamingCat]           = useState(null);
  const [renameValue, setRenameValue]           = useState("");
  const [catActionLoading, setCatActionLoading] = useState(null);

  const lastCursor     = useRef({ blockIndex: 0, cursorPos: 0 });
  const textareaRefs   = useRef({});
  const fileInputRef   = useRef();
  const renameInputRef = useRef();

  // Populate form on edit
  useEffect(() => {
    if (editItem) {
      setForm({ category: editItem.category || localCategories[0] });
      if (editItem.blocks?.length > 0) {
        setBlocks(editItem.blocks);
      } else {
        const migrated = [];
        if (editItem.answer) migrated.push({ type: "text", value: editItem.answer });
        editItem.screenshots?.forEach(src => migrated.push({ type: "image", value: src }));
        setBlocks(migrated.length > 0 ? migrated : [{ type: "text", value: "" }]);
      }
      setNewCategory("");
    } else {
      setForm({ category: "" });
      setBlocks([{ type: "text", value: "" }]);
    }
  }, [editItem]);

  // Auto-focus rename input
  useEffect(() => {
    if (renamingCat && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingCat]);

  // Cursor tracking
  const saveCursor = (blockIndex) => {
    const el = textareaRefs.current[blockIndex];
    if (el) lastCursor.current = { blockIndex, cursorPos: el.selectionStart };
  };

  // Paste image
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          insertImageAtCursor(item.getAsFile());
          return;
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [blocks]);

  const insertImageAtCursor = (file) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { alert("Image too large! Max 1.5MB."); return; }
    const { blockIndex, cursorPos } = lastCursor.current;
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setBlocks(prev => {
        const updated = [...prev];
        const target = updated[blockIndex];
        if (target?.type === "text") {
          const before = target.value.slice(0, cursorPos);
          const after  = target.value.slice(cursorPos);
          updated.splice(blockIndex, 1,
            { type: "text",  value: before },
            { type: "image", value: base64 },
            { type: "text",  value: after  }
          );
          const afterIdx = blockIndex + 2;
          setTimeout(() => {
            const el = textareaRefs.current[afterIdx];
            if (el) {
              el.focus();
              el.setSelectionRange(0, 0);
              lastCursor.current = { blockIndex: afterIdx, cursorPos: 0 };
            }
          }, 50);
        } else {
          updated.splice(blockIndex + 1, 0,
            { type: "image", value: base64 },
            { type: "text",  value: "" }
          );
        }
        return updated;
      });
    };
    reader.readAsDataURL(file);
  };

  const updateTextBlock = (index, value) =>
    setBlocks(prev => prev.map((b, i) => i === index ? { ...b, value } : b));

  const removeBlock = (index) =>
    setBlocks(prev => {
      const u = prev.filter((_, i) => i !== index);
      return u.length ? u : [{ type: "text", value: "" }];
    });

  // Search
  const loadAllData = async () => {
    if (allData.length > 0) return allData;
    const snap = await getDocs(collection(db, "finaltouch"));
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setAllData(data);
    return data;
  };

  const handleSearch = async (text) => {
    setSearchText(text);
    if (!text.trim()) { setSearchDropdown(false); setSuggestions([]); return; }
    const data  = allData.length > 0 ? allData : await loadAllData();
    const lower = text.toLowerCase();
    const matched = data.filter(item => {
      const ans = item.blocks
        ? item.blocks.filter(b => b.type === "text").map(b => b.value).join(" ")
        : item.answer || "";
      return item.question?.toLowerCase().includes(lower) || ans.toLowerCase().includes(lower);
    });
    setSuggestions(matched.slice(0, 8));
    setSearchDropdown(matched.length > 0);
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalCategory = form.category === "new" ? newCategory.trim() : form.category;
    if (!finalCategory) return;

    const answerText = blocks.filter(b => b.type === "text").map(b => b.value).join("\n");
    const payload = {
      answer: answerText,
      blocks,
      category: finalCategory,
      screenshots: blocks.filter(b => b.type === "image").map(b => b.value),
    };

    if (editItem) {
      await onUpdate?.(payload);
      setForm({ category: "" });
      setBlocks([{ type: "text", value: "" }]);
      setNewCategory("");
      return;
    }

    if (form.category === "new" && newCategory.trim()) {
      setLoading(true);
      try {
        await addCategoryToFirebase(newCategory.trim());
        const added = newCategory.trim();
        // ✅ Update local list immediately
        setLocalCategories(prev => [...prev, added]);
        onCategoryAdded?.(added);
      } catch (err) {
        console.error(err);
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    onSubmit(payload);
    setForm({ category: localCategories[0] });
    setBlocks([{ type: "text", value: "" }]);
    setNewCategory("");
  };

  // ── RENAME ──────────────────────────────────────────────────
  const handleRenameCommit = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === renamingCat) {
      setRenamingCat(null);
      setRenameValue("");
      return;
    }

    const oldName = renamingCat;
    setCatActionLoading(oldName);

    try {
      // 1. Update settings/categories list array in Firebase
      await renameCategoryInFirebase(oldName, trimmed);

      // 2. Batch-update all finaltouch items using old category name
      const itemsSnap = await getDocs(
        query(collection(db, "finaltouch"), where("category", "==", oldName))
      );
      if (!itemsSnap.empty) {
        const batch = writeBatch(db);
        itemsSnap.docs.forEach(d => batch.update(d.ref, { category: trimmed }));
        await batch.commit();
      }

      // 3. ✅ Update local list instantly — no refresh needed
      const updated = localCategories.map(c => c === oldName ? trimmed : c);
      setLocalCategories(updated);
      onCategoriesChanged?.(updated); // optional parent sync

      // 4. Sync dropdown selection if renamed category was selected
      if (form.category === oldName) setForm(f => ({ ...f, category: trimmed }));

    } catch (err) {
      console.error("Rename failed:", err);
      alert("❌ Rename failed — check console.");
    }

    setCatActionLoading(null);
    setRenamingCat(null);
    setRenameValue("");
  };

  // ── DELETE ──────────────────────────────────────────────────
  const handleDeleteCategory = async (catName) => {
    if (!window.confirm(`Delete category "${catName}"?\n\nItems under this category are kept.`)) return;

    setCatActionLoading(catName);

    try {
      // 1. Remove from settings/categories list array in Firebase
      await removeCategoryFromFirebase(catName);

      // 2. ✅ Update local list instantly — no refresh needed
      const updated = localCategories.filter(c => c !== catName);
      setLocalCategories(updated);
      onCategoriesChanged?.(updated); // optional parent sync

      // 3. Clear dropdown selection if deleted category was selected
      if (form.category === catName) setForm({ category: "" });

    } catch (err) {
      console.error("Delete failed:", err);
      alert("❌ Delete failed — check console.");
    }

    setCatActionLoading(null);
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit}>

      {/* Edit mode banner */}
      {editItem && (
        <div style={{ background: "#fff7e6", border: "1px solid #ffc107", borderRadius: 6, padding: "8px 14px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14 }}>
          <span>✏️ <strong>Edit mode</strong> — click inside text where you want image, then click 📸</span>
          <button type="button"
            onClick={() => { onCancelEdit?.(); setForm({ category: localCategories[0] }); setBlocks([{ type: "text", value: "" }]); }}
            style={{ background: "none", border: "none", color: "#dc3545", cursor: "pointer", fontWeight: "bold", fontSize: 16 }}>
            ✕ Cancel
          </button>
        </div>
      )}

      {/* Search Bar */}
      {!editItem && (
        <div style={{ position: "relative", marginBottom: 12 }}>
          <input
            type="text"
            placeholder="🔍 Search questions or answers..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            onBlur={() => setTimeout(() => setSearchDropdown(false), 200)}
            style={{ width: "100%", padding: "8px", borderRadius: 6, border: "1px solid #ccc" }}
          />
          {searchDropdown && suggestions.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ddd", borderRadius: 6, zIndex: 999, maxHeight: 300, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
              {suggestions.map(item => (
                <div key={item.id}
                  onMouseDown={() => { onSearchResult?.([item]); setSearchText(item.question || item.answer?.slice(0, 50)); setSearchDropdown(false); }}
                  style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                  onMouseLeave={e => e.currentTarget.style.background = "white"}
                >
                  <div style={{ fontWeight: "bold", fontSize: 13 }}>❓ {item.question || "No question"}</div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>💬 {item.answer?.slice(0, 60)}...</div>
                  <div style={{ fontSize: 11, color: "white", background: "#4f46e5", borderRadius: 4, padding: "1px 6px", display: "inline-block", marginTop: 4 }}>{item.category}</div>
                </div>
              ))}
            </div>
          )}
          {searchDropdown && suggestions.length === 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ddd", borderRadius: 6, zIndex: 999, padding: "10px 14px", color: "#999", fontSize: 14 }}>No results found</div>
          )}
        </div>
      )}

      {/* Rich Content Editor */}
      <div style={{ border: "1px solid #ddd", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
        <div style={{ padding: "7px 12px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>📝 <strong>Click inside text</strong> where you want the image → then click 📸</span>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => { Array.from(e.target.files).forEach(f => insertImageAtCursor(f)); e.target.value = ""; }}
              style={{ display: "none" }}
            />
            <button type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                const el = textareaRefs.current[lastCursor.current.blockIndex];
                if (el) lastCursor.current.cursorPos = el.selectionStart;
                fileInputRef.current?.click();
              }}
              style={{ padding: "5px 14px", fontSize: 12, background: "#4f46e5", color: "white", border: "none", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
            >📸 Insert Image at Cursor</button>
          </div>
        </div>

        <div style={{ padding: 10 }}>
          {blocks.map((block, index) => (
            <div key={index}>
              {block.type === "text" && (
                <div style={{ position: "relative", marginBottom: 4 }}>
                  <textarea
                    ref={el => textareaRefs.current[index] = el}
                    value={block.value}
                    onChange={(e) => { updateTextBlock(index, e.target.value); saveCursor(index); }}
                    onClick={() => saveCursor(index)}
                    onKeyUp={() => saveCursor(index)}
                    onFocus={() => saveCursor(index)}
                    placeholder={
                      blocks.filter((b, i) => b.type === "text" && i <= index).length === 1
                        ? "Type here... click where you want image, then click 📸 above"
                        : "Continue typing..."
                    }
                    rows={3}
                    style={{ width: "100%", padding: 8, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit", background: "#fafafa" }}
                  />
                  {blocks.length > 1 && (
                    <button type="button" onClick={() => removeBlock(index)}
                      style={{ position: "absolute", top: 4, right: 4, background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 4, fontSize: 10, padding: "1px 6px", cursor: "pointer" }}
                    >✕ remove</button>
                  )}
                </div>
              )}
              {block.type === "image" && (
                <div style={{ position: "relative", margin: "8px 0", border: "2px solid #e0e7ff", borderRadius: 8, display: "inline-block", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", maxWidth: "100%" }}>
                  <img src={block.value} alt={`img-${index}`} style={{ display: "block", maxWidth: "100%", maxHeight: 280, objectFit: "contain" }} />
                  <button type="button" onClick={() => removeBlock(index)}
                    style={{ position: "absolute", top: 6, right: 6, background: "rgba(220,38,38,0.85)", color: "white", border: "none", borderRadius: "50%", width: 22, height: 22, fontSize: 11, cursor: "pointer", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >✕</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Category select + Manage button */}
      <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center",width:"100%",flexWrap:"wrap" }}>
        <select
          value={form.category || ""}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          style={{ flex: 1 }}
          disabled={!!editItem}
        >
          <option value="" disabled>Select Category</option>
          {!editItem && <option value="new">➕ Add New Category</option>}
          {/* ✅ Uses localCategories — updates instantly */}
          {[...localCategories].reverse().map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        {!editItem && (
          <button type="button" onClick={() => setShowCatManager(v => !v)}
            style={{ padding: "5px 10px", fontSize: 13, cursor: "pointer", background: showCatManager ? "#4f46e5" : "#f1f5f9", color: showCatManager ? "white" : "#475569", border: "1px solid #cbd5e1", borderRadius: 6, whiteSpace: "nowrap" }}
          >⚙️ Manage</button>
        )}
      </div>

      {/* Category Manager Panel */}
      {showCatManager && !editItem && (
        <div style={{ marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", padding: "10px 12px" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>CATEGORIES</div>

          {/* ✅ Uses localCategories — updates instantly */}
          {[...localCategories].reverse().map(cat => (
            <div key={cat} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 8px", borderRadius: 6, marginBottom: 4, background: "white", border: "1px solid #e2e8f0" }}>

              {renamingCat === cat ? (
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleRenameCommit();
                    if (e.key === "Escape") { setRenamingCat(null); setRenameValue(""); }
                  }}
                  style={{ flex: 1, border: "1px solid #4f46e5", borderRadius: 4, padding: "3px 7px", fontSize: 13, outline: "none", marginRight: 6 }}
                />
              ) : (
                <span style={{ flex: 1, fontSize: 13, color: "#1e293b" }}>{cat}</span>
              )}

              {catActionLoading === cat ? (
                <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 8 }}>saving…</span>
              ) : renamingCat === cat ? (
                <div style={{ display: "flex", gap: 4 }}>
                  <button type="button" onClick={handleRenameCommit} style={iconBtn("#22c55e")}>✓</button>
                  <button type="button" onClick={() => { setRenamingCat(null); setRenameValue(""); }} style={iconBtn("#94a3b8")}>✕</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 4 }}>
                  <button type="button" title="Rename" onClick={() => { setRenamingCat(cat); setRenameValue(cat); }} style={iconBtn("#4f46e5")}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button type="button" title="Delete" onClick={() => handleDeleteCategory(cat)} style={iconBtn("#ef4444")}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}

          {localCategories.length === 0 && (
            <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "8px 0" }}>No categories yet</div>
          )}
        </div>
      )}

      {form.category === "new" && !editItem && (
        <input
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="Enter new category"
          style={{ width: "100%", marginTop: 8 }}
        />
      )}

      <button type="submit" style={{ marginTop: 8 }} disabled={loading}>
        {loading ? "Saving..." : editItem ? "💾 Save Changes" : "Add"}
      </button>
    </form>
  );
}

function iconBtn(color) {
  return {
    width: 26, height: 26, padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: `${color}18`, color,
    border: `1px solid ${color}55`,
    borderRadius: 5, cursor: "pointer", flexShrink: 0,
  };
}

export default Interviewform;